import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getCategoryById } from '@/mocks/categories';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <main className="min-h-screen pt-20">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900">文章不存在</h1>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const category = getCategoryById(article.categoryId);
  const relatedArticles = getRelatedArticles(article.slug, article.categoryId, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    inLanguage: 'zh-CN',
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: article.title,
    description: article.excerpt,
  };

  return (
    <main className="min-h-screen pt-24">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category
                ? [{ label: category.name, href: `/category/${category.slug}` }]
                : []),
              { label: article.title },
            ]}
          />
        </div>

        <article>
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-primary-500 bg-primary-50 px-2.5 py-1 rounded-full font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>
            <div className="mt-4 flex items-center gap-3 text-sm text-foreground-500 flex-wrap">
              <span>{article.author}</span>
              <span className="text-foreground-300">|</span>
              <span>{article.publishDate}</span>
              <span className="text-foreground-300">|</span>
              <span>{article.readTime} 分钟阅读</span>
            </div>
          </div>

          <div className="mb-8 rounded-lg overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-auto object-cover"
              title={`${article.title} - 火影同人指南`}
            />
          </div>

          <div
            className="prose-content text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <style>{`
            .prose-content h2 {
              font-family: var(--font-heading);
              font-size: 1.5rem;
              font-weight: 700;
              color: oklch(var(--foreground-900));
              margin-top: 2.5rem;
              margin-bottom: 1rem;
              padding-bottom: 0.5rem;
              border-bottom: 2px solid oklch(var(--background-200));
            }
            .prose-content h3 {
              font-family: var(--font-heading);
              font-size: 1.2rem;
              font-weight: 600;
              color: oklch(var(--foreground-800));
              margin-top: 2rem;
              margin-bottom: 0.75rem;
            }
            .prose-content p {
              margin-bottom: 1.25rem;
              line-height: 1.85;
              font-size: 1rem;
            }
            .prose-content ul, .prose-content ol {
              margin-bottom: 1.25rem;
              padding-left: 1.5rem;
            }
            .prose-content li {
              margin-bottom: 0.5rem;
              line-height: 1.75;
            }
            .prose-content strong {
              color: oklch(var(--foreground-900));
              font-weight: 600;
            }
          `}</style>
        </article>

        {category && (
          <div className="mt-8 pt-6 border-t border-background-200">
            <span className="text-sm text-foreground-500">
              分类：
              <Link
                to={`/category/${category.slug}`}
                className="text-primary-500 hover:text-primary-600 ml-1 cursor-pointer"
              >
                {category.name}
              </Link>
            </span>
          </div>
        )}

        {relatedArticles.length > 0 && (
          <section className="mt-12 mb-16">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((rel) => (
                <ArticleCard key={rel.id} article={rel} />
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}